import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Search, Loader2, Calendar, Clock, Scissors, ArrowRight } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function RescheduleLookupPage() {
  const navigate = useNavigate();
  const [phone, setPhone] = useState("");
  const [loading, setLoading] = useState(false);
  const [bookings, setBookings] = useState([]);
  const [searched, setSearched] = useState(false);
  const [error, setError] = useState("");

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!phone.trim()) return;
    
    setLoading(true);
    setError("");
    setSearched(true);
    
    try {
      const response = await fetch(`${API_URL}/api/bookings/lookup?phone=${encodeURIComponent(phone)}`);
      if (response.ok) {
        const data = await response.json();
        setBookings(data);
      } else {
        setError("Could not find bookings. Please check your phone number.");
      }
    } catch (err) {
      setError("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (time) => {
    const [hours, minutes] = time.split(":");
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? "PM" : "AM";
    const displayHour = hour > 12 ? hour - 12 : hour === 0 ? 12 : hour;
    return `${displayHour}:${minutes} ${ampm}`;
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr + "T00:00:00");
    return date.toLocaleDateString("en-US", {
      weekday: "short",
      month: "short",
      day: "numeric",
    });
  };

  return (
    <div className="min-h-screen bg-zinc-950 flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-white tracking-wider mb-2" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
            SHOWTIME SHOBI
          </h1>
          <p className="text-zinc-500 text-sm uppercase tracking-widest">Reschedule Your Appointment</p>
        </div>

        {/* Search Card */}
        <Card className="bg-zinc-900 border-zinc-800 mb-6">
          <CardContent className="pt-6">
            <form onSubmit={handleSearch} className="space-y-4">
              <div>
                <label className="text-sm text-zinc-400 mb-2 block">Enter your phone number</label>
                <Input
                  type="tel"
                  placeholder="647-123-4567"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  className="bg-zinc-800 border-zinc-700 text-white text-lg h-12"
                  data-testid="phone-input"
                />
              </div>
              <Button 
                type="submit" 
                disabled={loading || !phone.trim()}
                className="w-full bg-white text-black hover:bg-zinc-200 h-12"
                data-testid="search-btn"
              >
                {loading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <>
                    <Search className="w-5 h-5 mr-2" />
                    Find My Booking
                  </>
                )}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Results */}
        {searched && !loading && (
          <>
            {error ? (
              <Card className="bg-red-950/20 border-red-900/50">
                <CardContent className="pt-6 pb-6 text-center">
                  <p className="text-red-400">{error}</p>
                </CardContent>
              </Card>
            ) : bookings.length === 0 ? (
              <Card className="bg-zinc-900 border-zinc-800">
                <CardContent className="pt-6 pb-6 text-center">
                  <p className="text-zinc-400">No upcoming appointments found for this number.</p>
                  <Button 
                    onClick={() => navigate("/")} 
                    variant="outline" 
                    className="mt-4 border-zinc-700"
                  >
                    Book New Appointment
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-3">
                <p className="text-zinc-400 text-sm text-center mb-4">
                  Found {bookings.length} upcoming appointment{bookings.length > 1 ? "s" : ""}
                </p>
                {bookings.map((booking) => (
                  <Card 
                    key={booking.id} 
                    className="bg-zinc-900 border-zinc-800 hover:border-zinc-700 transition-colors cursor-pointer"
                    onClick={() => navigate(`/reschedule/${booking.cancel_token}`)}
                  >
                    <CardContent className="pt-4 pb-4">
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 text-white font-medium mb-1">
                            <Scissors className="w-4 h-4 text-zinc-500" />
                            {booking.service_name}
                          </div>
                          <div className="flex items-center gap-4 text-sm text-zinc-400">
                            <span className="flex items-center gap-1">
                              <Calendar className="w-3 h-3" />
                              {formatDate(booking.date)}
                            </span>
                            <span className="flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              {formatTime(booking.time)}
                            </span>
                          </div>
                        </div>
                        <ArrowRight className="w-5 h-5 text-zinc-500" />
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </>
        )}

        {/* Footer */}
        <p className="text-center text-zinc-600 text-xs mt-8">
          Need help? DM us on Instagram{" "}
          <a href="https://instagram.com/showtime_shobi" className="text-zinc-400 hover:text-white">
            @showtime_shobi
          </a>
        </p>
      </div>
    </div>
  );
}
